import { useState, useEffect, useCallback, useRef } from "react";
import {
  Activity, RefreshCw, Monitor, ChevronDown, ChevronRight,
  CheckCircle, XCircle, AlertTriangle, HelpCircle, Loader2, Wifi, WifiOff,
  Shield, Wrench, Search, BarChart3, Timer, Radio, Trash2
} from "lucide-react";
import api from "@/lib/api";

/* ───────── Small Helpers ───────── */

function StatusDot({ status }) {
  const colors = {
    healthy: "bg-emerald-500", ok: "bg-emerald-500",
    warning: "bg-amber-500",
    critical: "bg-red-500", fail: "bg-red-500",
    unknown: "bg-zinc-500",
  };
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${colors[status] || colors.unknown}`} />;
}

function timeSince(iso) {
  if (!iso) return "Nie";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `vor ${Math.round(diff)}s`;
  if (diff < 3600) return `vor ${Math.round(diff / 60)}min`;
  if (diff < 86400) return `vor ${Math.round(diff / 3600)}h`;
  return `vor ${Math.round(diff / 86400)}d`;
}

/* ───────── Summary Card (clickable) ───────── */

function SummaryCard({ label, value, color, bgClass, icon: Icon, active, onClick }) {
  return (
    <button
      data-testid={`summary-card-${label.toLowerCase()}`}
      onClick={onClick}
      className={`flex items-center gap-3 p-4 rounded-xl border text-left transition-all w-full ${
        active
          ? `${bgClass || "bg-zinc-800"} ring-1 ring-white/20`
          : "bg-zinc-900/50 border-zinc-800 hover:border-zinc-600"
      }`}
    >
      <div className={`p-2.5 rounded-lg ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-white tabular-nums">{value}</p>
        <p className="text-xs text-zinc-400">{label}</p>
      </div>
    </button>
  );
}

/* ───────── Device Health Card ───────── */

function DeviceHealthCard({ device, onDiskCleanup, onRepairDevice, scanActive, responded }) {
  const [expanded, setExpanded] = useState(false);

  const statusConfig = {
    healthy: { badge: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", label: "Gesund" },
    warning: { badge: "bg-amber-500/15 text-amber-400 border-amber-500/30", label: "Warnung" },
    critical: { badge: "bg-red-500/15 text-red-400 border-red-500/30", label: "Kritisch" },
    unknown: { badge: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30", label: "Kein Report" },
  };
  const st = statusConfig[device.overall_health] || statusConfig.unknown;

  const hasDiskProblem = device.checks_critical > 0 || device.checks_warning > 0;

  return (
    <div
      data-testid={`device-card-${device.computer_name}`}
      className={`border rounded-xl overflow-hidden transition-all ${
        scanActive && responded ? "border-emerald-500/40 bg-emerald-500/5" :
        device.overall_health === "critical" ? "border-red-500/30 bg-red-500/5" :
        device.overall_health === "warning" ? "border-amber-500/20 bg-amber-500/5" :
        device.overall_health === "healthy" ? "border-emerald-500/20 bg-emerald-500/5" :
        "border-zinc-800 bg-zinc-900/40"
      }`}
    >
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors">
        <div className="flex items-center gap-3">
          {scanActive ? (
            responded
              ? <CheckCircle className="w-4 h-4 text-emerald-500" />
              : <Radio className="w-4 h-4 text-amber-400 animate-pulse" />
          ) : (
            <div className={`w-2.5 h-2.5 rounded-full ${device.is_online ? "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)]" : "bg-zinc-600"}`} />
          )}
          <Monitor className="w-4 h-4 text-zinc-500" />
          <span className="text-white font-medium">{device.computer_name}</span>
          <span className="text-xs text-zinc-600">{device.agent_version || ""}</span>
        </div>
        <div className="flex items-center gap-3">
          {/* Tool LEDs */}
          <div className="hidden sm:flex items-center gap-2">
            {[
              { label: "TV", running: device.tv_running },
              { label: "RD", running: device.rd_running },
              { label: "AD", running: device.ad_running },
            ].map(t => (
              <div key={t.label} className="flex items-center gap-1">
                <span className={`w-1.5 h-1.5 rounded-full ${t.running ? "bg-emerald-500" : "bg-zinc-600"}`} />
                <span className="text-[10px] text-zinc-500">{t.label}</span>
              </div>
            ))}
          </div>
          {(device.checks_ok > 0 || device.checks_warning > 0 || device.checks_critical > 0) && (
            <span className="text-xs text-zinc-500">{device.checks_ok}/{device.checks_ok + device.checks_warning + device.checks_critical}</span>
          )}
          <span className={`text-xs px-2 py-0.5 rounded-full border ${st.badge}`}>{st.label}</span>
          {device.last_check && <span className="text-[10px] text-zinc-600 hidden md:inline">{timeSince(device.last_check)}</span>}
          {expanded ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-zinc-800 p-4 space-y-3">
          {/* Tools */}
          <div className="flex items-center gap-4 text-sm">
            <span className="text-zinc-500">Tools:</span>
            {[
              { label: "TeamViewer", running: device.tv_running },
              { label: "RustDesk", running: device.rd_running },
              { label: "AnyDesk", running: device.ad_running },
            ].map(t => (
              <div key={t.label} className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${t.running ? "bg-emerald-500" : "bg-zinc-600"}`} />
                <span className={t.running ? "text-zinc-300" : "text-zinc-500"}>{t.label}</span>
              </div>
            ))}
          </div>

          {/* Individual Health Checks */}
          {device.checks && device.checks.length > 0 && (
            <div className="space-y-1 mt-2">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1">Alle Checks ({device.checks.length})</p>
              <div className="grid gap-1">
                {device.checks
                  .sort((a, b) => {
                    const order = { critical: 0, warning: 1, ok: 2 };
                    return (order[a.status] ?? 2) - (order[b.status] ?? 2);
                  })
                  .map((c, idx) => (
                  <div key={idx} className={`flex items-center justify-between px-3 py-1.5 rounded text-xs ${
                    c.status === "critical" ? "bg-red-500/10 border border-red-500/20" :
                    c.status === "warning" ? "bg-amber-500/10 border border-amber-500/20" :
                    "bg-zinc-800/50 border border-zinc-700/30"
                  }`}>
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        c.status === "critical" ? "bg-red-500" : c.status === "warning" ? "bg-amber-500" : "bg-emerald-500"
                      }`} />
                      <span className={`font-medium truncate ${
                        c.status === "critical" ? "text-red-400" : c.status === "warning" ? "text-amber-400" : "text-zinc-400"
                      }`}>{c.name}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      {c.message && <span className="text-zinc-500 truncate max-w-[200px]">{c.message}</span>}
                      {c.auto_repaired && (
                        <span className="flex items-center gap-0.5 text-blue-400 whitespace-nowrap">
                          <Wrench className="w-3 h-3" />Repariert
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          {hasDiskProblem && device.is_online && (
            <button
              data-testid={`disk-cleanup-${device.computer_name}`}
              onClick={(e) => { e.stopPropagation(); onDiskCleanup(device.id, device.computer_name); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-orange-500/30 text-orange-400 hover:bg-orange-500/10 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />Disk bereinigen
            </button>
          )}
          {hasDiskProblem && device.is_online && (
            <button
              data-testid={`repair-device-${device.computer_name}`}
              onClick={(e) => { e.stopPropagation(); onRepairDevice(device.id, device.computer_name); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <Wrench className="w-3.5 h-3.5" />Reparieren
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ───────── Check Stats Bar ───────── */

function CheckStatsBar({ stat }) {
  const total = stat.total || 1;
  const pctOk = (stat.ok / total) * 100;
  const pctWarn = (stat.warning / total) * 100;
  const pctCrit = (stat.critical / total) * 100;
  const hasProblems = stat.critical > 0 || stat.warning > 0;

  return (
    <div data-testid={`check-stat-${stat.name}`} className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-zinc-200 font-medium">{stat.name}</span>
        <div className="flex items-center gap-2 text-xs">
          {stat.critical > 0 && <span className="text-red-400">{stat.critical} Kritisch</span>}
          {stat.warning > 0 && <span className="text-amber-400">{stat.warning} Warnung</span>}
          <span className="text-emerald-400">{stat.ok} OK</span>
          <span className="text-zinc-600">/ {stat.total}</span>
        </div>
      </div>
      <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden flex">
        {pctOk > 0 && <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pctOk}%` }} />}
        {pctWarn > 0 && <div className="h-full bg-amber-500 transition-all" style={{ width: `${pctWarn}%` }} />}
        {pctCrit > 0 && <div className="h-full bg-red-500 transition-all" style={{ width: `${pctCrit}%` }} />}
      </div>
      {hasProblems && stat.affected_devices?.length > 0 && (
        <div className="pl-3 pt-1 space-y-0.5">
          {stat.affected_devices.map((ad, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <StatusDot status={ad.status} />
              <span className="text-zinc-400">{ad.computer_name}</span>
              {ad.message && <span className="text-zinc-600 truncate max-w-[250px]">{ad.message}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════ */

export default function HealthCenterPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [activeTab, setActiveTab] = useState("devices");
  const [monitoredFolders, setMonitoredFolders] = useState([]);
  const [newFolder, setNewFolder] = useState("");

  const fetchFolders = async () => {
    try {
      const res = await api.get("/tool-config/monitored-folders");
      setMonitoredFolders(res.data.folders || []);
    } catch {}
  };

  const addFolder = async () => {
    const path = newFolder.trim();
    if (!path) return;
    const updated = [...monitoredFolders, path];
    try {
      await api.post("/tool-config/monitored-folders", { folders: updated });
      setMonitoredFolders(updated);
      setNewFolder("");
    } catch {}
  };

  const removeFolder = async (idx) => {
    const updated = monitoredFolders.filter((_, i) => i !== idx);
    try {
      await api.post("/tool-config/monitored-folders", { folders: updated });
      setMonitoredFolders(updated);
    } catch {}
  };

  // ── Scan tracking ──
  const [scanActive, setScanActive] = useState(false);
  const [scanElapsed, setScanElapsed] = useState(0);
  const [respondedDevices, setRespondedDevices] = useState([]);
  const [scanTotal, setScanTotal] = useState(0);
  const scanStartRef = useRef(null);
  const scanSnapshotRef = useRef(null);
  const scanTimerRef = useRef(null);
  const scanPollRef = useRef(null);

  const fetchData = useCallback(async (showLoader = true) => {
    if (showLoader) setRefreshing(true);
    try {
      const res = await api.get("/health/overview");
      setData(res.data);
    } catch (err) {
      console.error("Health overview fetch failed:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); fetchFolders(); }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh || scanActive) return;
    const iv = setInterval(() => fetchData(false), 30000);
    return () => clearInterval(iv);
  }, [autoRefresh, fetchData, scanActive]);

  // Scan timer
  useEffect(() => {
    if (!scanActive) return;
    scanTimerRef.current = setInterval(() => {
      setScanElapsed(Math.round((Date.now() - scanStartRef.current) / 1000));
    }, 1000);
    return () => clearInterval(scanTimerRef.current);
  }, [scanActive]);

  // Scan polling via lightweight endpoint
  useEffect(() => {
    if (!scanActive || !scanSnapshotRef.current) return;
    const poll = async () => {
      try {
        const res = await api.post("/health/scan-status", { snapshot: scanSnapshotRef.current });
        const { responded, responded_count } = res.data;
        setRespondedDevices(responded);
        if (responded_count >= scanTotal && scanTotal > 0) endScan(responded_count, scanTotal);
      } catch (err) {
        console.error("Scan poll failed:", err);
      }
    };
    scanPollRef.current = setInterval(poll, 5000);
    poll();
    return () => clearInterval(scanPollRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanActive, scanTotal]);

  // Auto-end after 120s
  useEffect(() => {
    if (scanActive && scanElapsed >= 120) endScan(respondedDevices.length, scanTotal);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanElapsed, scanActive]);

  const endScan = (responded = 0, total = 0) => {
    setScanActive(false);
    clearInterval(scanTimerRef.current);
    clearInterval(scanPollRef.current);
    fetchData(false);
  };

  const startScan = async () => {
    try {
      const res = await api.post("/health/trigger", { device_ids: [] });
      const { triggered, snapshot } = res.data;
      if (triggered === 0) return;
      scanSnapshotRef.current = snapshot;
      scanStartRef.current = Date.now();
      setScanTotal(triggered);
      setScanElapsed(0);
      setRespondedDevices([]);
      setScanActive(true);
    } catch (err) {
      console.error("Scan trigger failed:", err);
    }
  };

  const triggerDiskCleanup = async (deviceId, computerName) => {
    try {
      await api.post(`/health/disk-cleanup/${deviceId}`);
    } catch (err) {
      console.error("Disk cleanup failed:", err);
    }
  };

  const triggerRepairDevice = async (deviceId, computerName) => {
    try {
      const res = await api.post(`/device/trigger-repair-all/${deviceId}`);
      console.log(`Repair triggered for ${computerName}:`, res.data);
    } catch (err) {
      console.error("Repair failed:", err);
    }
  };

  const [repairingAll, setRepairingAll] = useState(false);
  const triggerRepairAllDevices = async () => {
    const devicesWithProblems = (data?.devices || []).filter(d =>
      d.is_online && d.overall_health !== "healthy" && d.overall_health !== "unknown"
    );
    if (devicesWithProblems.length === 0) return;
    setRepairingAll(true);
    let success = 0, failed = 0;
    for (const device of devicesWithProblems) {
      try {
        await api.post(`/device/trigger-repair-all/${device.id}`);
        success++;
      } catch { failed++; }
    }
    setRepairingAll(false);
  };

  /* ── Filtering ── */
  const respondedIds = new Set(respondedDevices.map(d => d.id));

  const filteredDevices = (data?.devices || []).filter((d) => {
    const matchSearch = d.computer_name.toLowerCase().includes(searchTerm.toLowerCase());
    if (filterStatus === "all") return matchSearch;
    if (filterStatus === "healthy") return matchSearch && d.overall_health === "healthy";
    if (filterStatus === "warning") return matchSearch && d.overall_health === "warning";
    if (filterStatus === "critical") return matchSearch && d.overall_health === "critical";
    if (filterStatus === "unknown") return matchSearch && d.overall_health === "unknown";
    return matchSearch;
  });

  const checkStats = data?.check_stats || [];
  const failingStats = checkStats.filter(s => s.critical > 0 || s.warning > 0);
  const passingStats = checkStats.filter(s => s.critical === 0 && s.warning === 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
        <span className="ml-2 text-zinc-400">Lade Health Center...</span>
      </div>
    );
  }

  const s = data?.summary || {};

  return (
    <div data-testid="health-center-page" className="space-y-6">
      {/* ══ Header ══ */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Shield className="w-8 h-8 text-red-500" />
            Health Center
          </h1>
          <p className="text-zinc-400 mt-1">Echtzeit-Monitoring aller Geräte und automatische Reparatur</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="toggle-auto-refresh"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors ${
              autoRefresh ? "border-emerald-500/40 text-emerald-400" : "border-zinc-700 text-zinc-500"
            }`}
          >
            {autoRefresh ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
            {autoRefresh ? "Live" : "Pause"}
          </button>
          <button
            data-testid="repair-all-global-btn"
            onClick={triggerRepairAllDevices}
            disabled={repairingAll}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-lg bg-orange-600 hover:bg-orange-700 text-white transition-colors disabled:opacity-50"
          >
            <Wrench className={`w-4 h-4 ${repairingAll ? "animate-spin" : ""}`} />Alle reparieren
          </button>
          <button
            data-testid="start-scan-btn"
            onClick={startScan}
            disabled={scanActive}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:opacity-50"
          >
            <Activity className="w-4 h-4" />Jetzt prüfen
          </button>
          <button
            data-testid="refresh-btn"
            onClick={() => fetchData()}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />Aktualisieren
          </button>
        </div>
      </div>

      {/* ══ Scan Banner ══ */}
      {scanActive && (
        <div data-testid="scan-banner" className="relative overflow-hidden rounded-xl border border-emerald-500/30 bg-gradient-to-r from-emerald-500/10 to-emerald-600/5 p-4">
          <div className="absolute inset-0 bg-emerald-500/5 animate-pulse" />
          <div className="relative flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Radio className="w-6 h-6 text-emerald-400 animate-pulse" />
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full animate-ping" />
              </div>
              <div>
                <p className="text-emerald-300 font-semibold text-sm">Live-Scan läuft...</p>
                <p className="text-emerald-400/60 text-xs">Agenten prüfen alle 30s ihren Heartbeat und führen den Check aus.</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <Timer className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-300 font-mono tabular-nums">{scanElapsed}s</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-300 font-mono tabular-nums">{respondedDevices.length}/{scanTotal}</span>
                <span className="text-emerald-400/60 text-xs">Antworten</span>
              </div>
              <div className="w-32 h-2.5 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all duration-700 rounded-full"
                  style={{ width: `${scanTotal > 0 ? (respondedDevices.length / scanTotal) * 100 : 0}%` }} />
              </div>
              <button onClick={() => endScan()} className="text-xs px-2.5 py-1 rounded-lg border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 transition-colors">
                Abbrechen
              </button>
            </div>
          </div>
          {respondedDevices.length > 0 && (
            <div className="relative mt-3 pt-3 border-t border-emerald-500/20">
              <div className="flex flex-wrap gap-2">
                {respondedDevices.map((d) => (
                  <span key={d.id} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-emerald-500/20 text-emerald-300 rounded-full">
                    <CheckCircle className="w-3 h-3" />{d.computer_name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ Summary Cards ══ */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Gesamt" value={s.total || 0} color="bg-zinc-700" bgClass="bg-zinc-800 border-zinc-600" icon={Monitor}
          active={filterStatus === "all"} onClick={() => setFilterStatus("all")} />
        <SummaryCard label="Gesund" value={s.healthy || 0} color="bg-emerald-600" bgClass="bg-emerald-500/15 border-emerald-500/40" icon={CheckCircle}
          active={filterStatus === "healthy"} onClick={() => setFilterStatus("healthy")} />
        <SummaryCard label="Warnung" value={s.warning || 0} color="bg-amber-600" bgClass="bg-amber-500/15 border-amber-500/40" icon={AlertTriangle}
          active={filterStatus === "warning"} onClick={() => setFilterStatus("warning")} />
        <SummaryCard label="Kritisch" value={s.critical || 0} color="bg-red-600" bgClass="bg-red-500/15 border-red-500/40" icon={XCircle}
          active={filterStatus === "critical"} onClick={() => setFilterStatus("critical")} />
      </div>

      {/* ══ Filter indicator ══ */}
      {filterStatus !== "all" && (
        <div className="flex items-center gap-2 text-sm text-zinc-400">
          <span>Filter:</span>
          <span className="px-2 py-0.5 rounded-full border border-red-500/30 text-red-400 text-xs capitalize">
            {filterStatus === "healthy" ? "Gesund" : filterStatus === "warning" ? "Warnung" : filterStatus === "critical" ? "Kritisch" : filterStatus}
          </span>
          <button onClick={() => setFilterStatus("all")} className="text-red-400 hover:text-red-300 underline text-xs">
            Zurücksetzen
          </button>
        </div>
      )}

      {/* ══ Tabs ══ */}
      <div className="flex items-center gap-1 border-b border-zinc-800">
        <button data-testid="tab-devices" onClick={() => setActiveTab("devices")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "devices" ? "border-red-500 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}>
          <Monitor className="w-4 h-4 inline mr-1.5 -mt-0.5" />Geräte ({filteredDevices.length})
        </button>
        <button data-testid="tab-folders" onClick={() => setActiveTab("folders")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "folders" ? "border-red-500 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}>Ordner-Monitoring</button>
        <button data-testid="tab-problems" onClick={() => setActiveTab("problems")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "problems" ? "border-red-500 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}>
          <BarChart3 className="w-4 h-4 inline mr-1.5 -mt-0.5" />Häufigste Probleme
          {failingStats.length > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-red-500/20 text-red-400 rounded">{failingStats.length}</span>
          )}
        </button>
      </div>

      {/* ══ Tab: Devices ══ */}
      {activeTab === "devices" && (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input data-testid="health-search" type="text" placeholder="Gerät suchen..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 bg-zinc-900/50 border border-zinc-800 rounded-xl text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-red-500/50 transition-colors" />
          </div>

          <div className="space-y-2">
            {filteredDevices.length === 0 ? (
              <div className="py-16 text-center">
                <Monitor className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500">Keine Geräte gefunden</p>
              </div>
            ) : (
              filteredDevices.map((device) => (
                <DeviceHealthCard key={device.id} device={device} onDiskCleanup={triggerDiskCleanup} onRepairDevice={triggerRepairDevice}
                  scanActive={scanActive} responded={respondedIds.has(device.id)} />
              ))
            )}
          </div>
        </>
      )}

      {/* ══ Tab: Ordner-Monitoring ══ */}
      {activeTab === "folders" && (
        <div className="space-y-4" data-testid="folders-tab">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-1">Ueberwachte Ordner</h3>
            <p className="text-zinc-500 text-sm mb-4">Diese Ordner werden bei jedem Health-Check auf allen Geraeten geprueft. Fehlende Ordner werden automatisch angelegt.</p>
            
            <div className="flex gap-2 mb-4">
              <input value={newFolder} onChange={e => setNewFolder(e.target.value)}
                placeholder="z.B. C:\TSR\Daten" onKeyDown={e => e.key === "Enter" && addFolder()}
                className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm font-mono placeholder:text-zinc-600"
                data-testid="add-folder-input" />
              <button onClick={addFolder} disabled={!newFolder.trim()}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm rounded font-medium"
                data-testid="add-folder-btn">Hinzufuegen</button>
            </div>

            {monitoredFolders.length === 0 ? (
              <p className="text-zinc-500 text-sm italic">Keine Ordner konfiguriert.</p>
            ) : (
              <div className="space-y-2">
                {monitoredFolders.map((f, i) => (
                  <div key={i} className="flex items-center justify-between bg-zinc-800/50 border border-zinc-700 rounded px-4 py-3">
                    <span className="text-white text-sm font-mono">{f}</span>
                    <button onClick={() => removeFolder(i)}
                      className="text-zinc-500 hover:text-red-400 transition-colors p-1" data-testid={`remove-folder-${i}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ Tab: Problems ══ */}
      {activeTab === "problems" && (
        <div className="space-y-6">
          {failingStats.length > 0 ? (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 space-y-5">
              <p className="text-sm font-semibold text-zinc-400 uppercase tracking-wider">Probleme erkannt</p>
              {failingStats.map((stat) => <CheckStatsBar key={stat.name} stat={stat} />)}
            </div>
          ) : (
            <div className="py-16 text-center rounded-xl border border-zinc-800 bg-zinc-900/40">
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <p className="text-emerald-400 font-medium">Keine Probleme erkannt</p>
              <p className="text-zinc-500 text-sm mt-1">Alle Checks bestanden.</p>
            </div>
          )}

          {passingStats.length > 0 && (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 space-y-5">
              <p className="text-sm font-semibold text-emerald-500 uppercase tracking-wider">Alle Checks bestanden</p>
              {passingStats.map((stat) => <CheckStatsBar key={stat.name} stat={stat} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
